/**
 * Publishes an assembled web app and returns its public URL — the bookmark.
 *
 * No new infrastructure. `POST /apps/{appId}/upload` already takes an arbitrary
 * contentType, writes under the `apps/*&#47;uploads/*` prefix (which carries a public-read
 * bucket policy precisely so uploads are "displayable via a stable URL"), and returns the
 * durable `fileUrl`. So publishing is: presign → PUT → keep the URL.
 *
 * Unlike `makeAssetUploader`, every failure here is DISTINGUISHED rather than collapsed to
 * null. A silent null is survivable for a decorative image; for the publish step it is the
 * whole product failing with nothing to tell the user, which is the failure mode this
 * codebase has been bitten by repeatedly.
 */
export type PublishFailure =
  | 'not-signed-in'
  | 'no-backend'
  | 'presign-failed'
  | 'upload-failed'
  | 'network';

export class WebAppPublishError extends Error {
  readonly reason: PublishFailure;
  constructor(reason: PublishFailure, detail?: string) {
    super(MESSAGES[reason] + (detail ? ` (${detail})` : ''));
    this.name = 'WebAppPublishError';
    this.reason = reason;
  }
}

const MESSAGES: Record<PublishFailure, string> = {
  'not-signed-in': 'You need to be signed in to publish an app to the web.',
  'no-backend': 'No backend is configured, so there is nowhere to publish this app.',
  'presign-failed': 'The server would not accept the upload request.',
  'upload-failed': 'The app could not be uploaded.',
  network: 'Could not reach the server to publish this app.',
};

export interface PublishDeps {
  appId: string;
  baseUrl: string;
  getToken: () => string | null;
  fetchImpl?: typeof fetch;
}

export interface PublishResult {
  /** The public URL. This is the bookmark. */
  url: string;
  /** S3 key, kept so a later stable-key upload can overwrite this exact object. */
  key: string;
  bytes: number;
}

/** Signed and sent identically — S3 rejects a presigned PUT whose Content-Type differs. */
const CONTENT_TYPE = 'text/html; charset=utf-8';

export async function publishWebApp(html: string, deps: PublishDeps): Promise<PublishResult> {
  const doFetch = deps.fetchImpl ?? fetch;
  const token = deps.getToken();
  if (!deps.baseUrl || !deps.appId) throw new WebAppPublishError('no-backend');
  if (!token) throw new WebAppPublishError('not-signed-in');

  let presignRes: Response;
  try {
    presignRes = await doFetch(`${deps.baseUrl.replace(/\/+$/, '')}/apps/${deps.appId}/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ fileName: 'index.html', contentType: CONTENT_TYPE }),
    });
  } catch (e) {
    throw new WebAppPublishError('network', (e as Error)?.message);
  }
  if (!presignRes.ok) throw new WebAppPublishError('presign-failed', `HTTP ${presignRes.status}`);

  const body = (await presignRes.json()) as { uploadUrl?: string; fileUrl?: string; key?: string };
  if (!body.uploadUrl || !body.fileUrl) throw new WebAppPublishError('presign-failed', 'no upload URL returned');

  let put: Response;
  try {
    put = await doFetch(body.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': CONTENT_TYPE },
      body: html,
    });
  } catch (e) {
    throw new WebAppPublishError('network', (e as Error)?.message);
  }
  if (!put.ok) throw new WebAppPublishError('upload-failed', `HTTP ${put.status}`);

  return { url: body.fileUrl, key: body.key ?? '', bytes: html.length };
}
